import { describeError } from './errors';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const refreshToken = localStorage.getItem('admin_refresh_token');
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;

    const data = await res.json();
    localStorage.setItem('admin_token', data.token);
    localStorage.setItem('admin_refresh_token', data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('admin_token');
  // FormData must go without an explicit Content-Type: the browser sets
  // multipart/form-data with the boundary itself.
  const contentType: Record<string, string> =
    options?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' };
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...contentType,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (res.status === 401) {
    // Deduplicate concurrent refresh attempts
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = tryRefresh().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }

    const refreshed = await refreshPromise;
    if (refreshed) {
      // Retry with the new token
      const newToken = localStorage.getItem('admin_token');
      const retry = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
          ...contentType,
          ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
          ...options?.headers,
        },
      });
      if (retry.ok) return retry.status === 204 ? (undefined as T) : retry.json();
    }

    // Refresh failed — clear tokens and redirect to login
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    window.location.href = '/login';
    throw new Error('Сессия истекла');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(describeError(res.status, body.error), { cause: body.error });
  }
  // A 204 (e.g. deleting a group) has no body, and parsing it would throw.
  if (res.status === 204) return undefined as T;
  return res.json();
}

export interface AppSetting {
  key: string;
  value: string;
  updated_at: string;
}

export type UserType = 'player' | 'coach' | 'parent' | 'admin';

export interface FeatureOverride {
  feature_key: string;
  user_type: UserType;
  user_id: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface TargetUser {
  id: string;
  name: string;
}

export const api = {
  // Auth — bypasses request() so a 401 from a wrong password doesn't trigger
  // the session-expired refresh+reload path (there's nothing to refresh yet).
  adminLogin: async (password: string) => {
    const res = await fetch(`${API_BASE}/api/auth/admin/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || (res.status === 401 ? 'Неверный пароль' : `HTTP ${res.status}`));
    }
    const data = (await res.json()) as { token: string; refresh_token: string };
    localStorage.setItem('admin_token', data.token);
    localStorage.setItem('admin_refresh_token', data.refresh_token);
    return data;
  },

  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    window.location.href = '/login';
  },

  // Settings
  listSettings: () => request<AppSetting[]>('/api/settings/'),

  upsertSetting: (key: string, value: string) =>
    request<{ status: string }>('/api/settings/', {
      method: 'PUT',
      body: JSON.stringify({ key, value }),
    }),

  batchUpsertSettings: (items: { key: string; value: string }[]) =>
    request<{ status: string }>('/api/settings/batch', {
      method: 'PUT',
      body: JSON.stringify(items),
    }),

  deleteSetting: (key: string) =>
    request<{ status: string }>(`/api/settings/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),

  // Feature toggles
  getFeatureToggles: () =>
    request<Record<string, boolean>>('/api/settings/feature-toggles'),

  // Feature toggle per-user overrides
  listFeatureOverrides: (key: string) =>
    request<FeatureOverride[] | null>(
      `/api/settings/feature-toggles/${encodeURIComponent(key)}/overrides`,
    ),

  upsertFeatureOverride: (
    key: string,
    userType: UserType,
    userID: string,
    enabled: boolean,
  ) =>
    request<{ status: string }>(
      `/api/settings/feature-toggles/${encodeURIComponent(key)}/overrides`,
      {
        method: 'POST',
        body: JSON.stringify({ user_type: userType, user_id: userID, enabled }),
      },
    ),

  deleteFeatureOverride: (key: string, userType: UserType, userID: string) =>
    request<{ status: string }>(
      `/api/settings/feature-toggles/${encodeURIComponent(key)}/overrides/${userType}/${encodeURIComponent(userID)}`,
      { method: 'DELETE' },
    ),

  // Users — used to populate the override target picker. Returned shape
  // varies per endpoint, so each call is narrowed to {id, name} at the call site.
  // Inactive players come too: the admin restores them from the same list.
  listPlayers: () => request<Player[]>('/api/players/'),

  updatePlayer: (
    id: string,
    changes: Partial<{
      name: string;
      login: string;
      password: string;
      birth_date: string | null;
      group_id: string | null;
      medical_cert_expiry: string | null;
    }>,
  ) =>
    request<Player>(`/api/players/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(changes),
    }),

  // The server's DELETE only deactivates: the child leaves the team chat, their
  // sessions end and unpaid invoices are cancelled, but the data stays.
  deactivatePlayer: (id: string) =>
    request<{ ok: boolean }>(`/api/players/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  restorePlayer: (id: string) =>
    request<Player>(`/api/players/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: true }),
    }),

  listPlayerAttendance: () =>
    request<Array<{
      player_id: string;
      player_name: string;
      total_sessions: number;
      attended: number;
    }> | null>('/api/stats/player-attendance'),
  listCoaches: () => request<Coach[]>('/api/coaches/'),

  createCoach: (input: CoachInput & { password: string }) =>
    request<Coach>('/api/coaches/', { method: 'POST', body: JSON.stringify(input) }),

  // The server changes only the fields sent, so the salary and the password
  // stay as they are unless they are passed.
  updateCoach: (id: number, changes: Partial<CoachInput> & { password?: string }) =>
    request<Coach>(`/api/coaches/${id}`, { method: 'PUT', body: JSON.stringify(changes) }),

  // A coach logs in by password alone, so two coaches must never share one.
  isCoachPasswordTaken: async (password: string) => {
    const data = await request<{ exists: boolean }>(
      `/api/coaches/check-password?${new URLSearchParams({ password })}`,
    );
    return data.exists;
  },

  deleteCoach: (id: number) => request<void>(`/api/coaches/${id}`, { method: 'DELETE' }),
  listParents: () => request<Array<{ id: string; name: string }>>('/api/parents/'),

  // Maintenance mode
  getMaintenanceMode: () =>
    request<MaintenanceModeConfig>('/api/settings/maintenance-mode'),

  // Player rewards — handoff queue of monthly winners (pending + selected).
  listAwaitingPlayerRewards: () =>
    request<PlayerRewardEntry[] | null>('/api/player-rewards/'),

  listRewards: () => request<Reward[] | null>('/api/rewards'),

  // Attaches the prize to the player's reward and decrements its stock.
  selectPlayerReward: (id: string, rewardID: string) =>
    request<{ ok: boolean }>(`/api/player-rewards/${encodeURIComponent(id)}/select`, {
      method: 'PUT',
      body: JSON.stringify({ reward_id: rewardID }),
    }),

  markPlayerRewardGiven: (id: string) =>
    request<{ ok: boolean }>(`/api/player-rewards/${encodeURIComponent(id)}/give`, {
      method: 'PUT',
    }),

  // Soft delete: the reward leaves the queue and the kid's app, a prize the
  // kid already picked goes back on the stock. Restorable.
  deletePlayerReward: (id: string) =>
    request<{ ok: boolean }>(`/api/player-rewards/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  restorePlayerReward: (id: string) =>
    request<{ ok: boolean }>(`/api/player-rewards/${encodeURIComponent(id)}/restore`, {
      method: 'PUT',
    }),

  listDeletedPlayerRewards: () =>
    request<DeletedPlayerRewardEntry[] | null>('/api/player-rewards/deleted'),

  // Players
  listGroups: () => request<Group[] | null>('/api/groups/'),

  createGroup: (input: { name: string; color: string }) =>
    request<Group>('/api/groups/', {
      method: 'POST',
      body: JSON.stringify({ name: input.name, description: null, color: input.color }),
    }),

  // The server overwrites every field, so the description the form doesn't
  // show is sent back as it was.
  updateGroup: (group: Group, changes: { name: string; color: string }) =>
    request<Group>(`/api/groups/${encodeURIComponent(group.id)}`, {
      method: 'PUT',
      body: JSON.stringify({ name: changes.name, description: group.description, color: changes.color }),
    }),

  deleteGroup: (id: string) =>
    request<void>(`/api/groups/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  createGroupChat: (groupID: string, groupName: string) =>
    request<{ chat: { id: string; name: string }; group: Group }>('/api/chats/for-group', {
      method: 'POST',
      body: JSON.stringify({ group_id: groupID, group_name: groupName }),
    }),

  createPlayer: (input: NewPlayer) =>
    request<Player>('/api/players/', {
      method: 'POST',
      // The server decodes is_active as a plain bool (false when omitted),
      // and inactive players are refused at login.
      body: JSON.stringify({ ...input, is_active: true }),
    }),

  uploadImage: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const data = await request<{ url: string }>('/api/upload/image', {
      method: 'POST',
      body: form,
    });
    return data.url;
  },

  deleteUploadedImage: (url: string) =>
    request<{ ok: boolean }>(`/api/upload/image?url=${encodeURIComponent(url)}`, {
      method: 'DELETE',
    }),
};

export interface MaintenancePlatformConfig {
  enabled: boolean;
  versions: string[];
}

export interface MaintenanceModeConfig {
  ios: MaintenancePlatformConfig;
  android: MaintenancePlatformConfig;
}

export type PlayerRewardStatus = 'pending' | 'selected';

export interface PlayerRewardEntry {
  id: string;
  player_id: string;
  player_name: string;
  group_id: string;
  group_name: string;
  month: string;
  position: number;
  status: PlayerRewardStatus;
  reward_id: string | null;
  reward_title: string | null;
  reward_image_url: string | null;
  created_at: string;
}

export interface DeletedPlayerRewardEntry {
  id: string;
  player_id: string;
  player_name: string;
  group_id: string;
  group_name: string;
  month: string;
  position: number;
  deleted_at: string;
  created_at: string;
}

export interface Reward {
  id: string;
  title: string;
  description: string | null;
  created_at: string;
  image_url: string | null;
  count: number;
}

export interface Coach {
  id: number;
  name: string;
  email: string | null;
  phone: string;
  password?: string;
  avatar_url: string | null;
  salary_per_training: number;
  birth_date: string | null;
  color: string;
  groups?: Group[];
  created_at: string;
  updated_at: string;
}

export interface CoachInput {
  name: string;
  phone: string;
  email: string | null;
  birth_date: string | null;
  color: string;
  group_ids: string[];
}

export interface Group {
  id: string;
  name: string;
  description: string | null;
  color: string;
  chat_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface NewPlayer {
  name: string;
  birth_date: string | null;
  login: string;
  password: string;
  group_id: string | null;
  medical_cert_expiry: string | null;
  medical_cert_url: string | null;
}

export interface Player {
  id: string;
  name: string;
  birth_date: string | null;
  login: string;
  password?: string;
  total_points: number;
  attendance_count: number;
  avatar_url: string | null;
  group_id: string | null;
  is_active: boolean;
  medical_cert_expiry: string | null;
  medical_cert_url: string | null;
  payment_start_date: string | null;
  created_at: string;
  updated_at: string;
}
